/**
 * Utility functions for time-based label formatting
 * When year === 2026, we display quarterly format:
 *   - Portuguese: 1T26, 1T25 (T = Trimestre)
 *   - English: 1Q26, 1Q25 (Q = Quarter)
 * When year === 2025 or earlier, we display annual format (2025, 2024, etc.)
 */

/**
 * Get the quarterly suffix based on language
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - 'T' for Portuguese, 'Q' for English
 */
const getQuarterPrefix = (language) => {
  return language === 'pt' ? 'T' : 'Q';
};

/**
 * Get the display label for the current period
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Formatted period label
 */
export const getCurrentPeriodLabel = (year, language = 'pt') => {
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return `1${q}26`;
  }
  return year.toString();
};

/**
 * Get the display label for the previous period (YoY comparison)
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Formatted period label
 */
export const getPreviousPeriodLabel = (year, language = 'pt') => {
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return `1${q}25`;
  }
  return (year - 1).toString();
};

/**
 * Get the budget label for the current period
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Formatted budget label
 */
export const getBudgetLabel = (year, language = 'pt') => {
  const prefix = language === 'pt' ? 'Orçamento' : 'Budget';
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return `${prefix} 1${q}26`;
  }
  return `${prefix} ${year}`;
};

/**
 * Get short budget label (for badges/pills)
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Short formatted budget label
 */
export const getShortBudgetLabel = (year, language = 'pt') => {
  const prefix = language === 'pt' ? 'Orç.' : 'Bud.';
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return `${prefix} 1${q}26`;
  }
  return `${prefix} ${year}`;
};

/**
 * Check if we're in quarterly mode (year === 2026)
 * @param {number} year - The fiscal year
 * @returns {boolean} - True if quarterly format should be used
 */
export const isQuarterlyMode = (year) => {
  return year === 2026;
};

/**
 * Get full comparison subtitle text
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Full comparison subtitle
 */
export const getComparisonSubtitle = (year, language = 'pt') => {
  const current = getCurrentPeriodLabel(year, language);
  const previous = getPreviousPeriodLabel(year, language);
  const budget = getBudgetLabel(year, language);
  
  if (language === 'pt') {
    return `vs ${budget} e comparativo com ${previous}`;
  }
  return `vs ${budget} and ${previous} comparison`;
};

/**
 * Get page title suffix (YTD label)
 * @param {number} year - The fiscal year (e.g., 2025, 2026)
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Title suffix
 */
export const getTitleSuffix = (year, language = 'pt') => {
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return `1${q}26`;
  }
  return `${year} YTD`;
};

/**
 * Get the vs budget label for KPI badges
 * @param {number} year - The fiscal year
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Budget comparison label
 */
export const getVsBudgetLabel = (year, language = 'pt') => {
  if (year === 2026) {
    const q = getQuarterPrefix(language);
    return language === 'pt' ? `vs Orç. 1${q}` : `vs Bud. 1${q}`;
  }
  return language === 'pt' ? 'vs Orç.' : 'vs Bud.';
};

/**
 * Get the YoY label for KPI badges
 * @param {number} year - The fiscal year
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - YoY comparison label
 */
export const getYoYLabel = (year, language = 'pt') => {
  if (year === 2026) {
    return `vs ${getPreviousPeriodLabel(year, language)}`;
  }
  return 'YoY';
};

/**
 * Replace year references in text with quarterly format when applicable
 * @param {string} text - Original text
 * @param {number} year - The fiscal year
 * @param {string} language - 'pt' or 'en'
 * @returns {string} - Text with replaced year references
 */
export const replaceYearReferences = (text, year, language = 'pt') => {
  if (year !== 2026) return text;
  
  const q = getQuarterPrefix(language);
  let result = text;
  
  // Replace "2025" with "1T26" or "1Q26" (current period in quarterly mode)
  result = result.replace(/\b2025\b/g, `1${q}26`);
  
  // Replace "2024" with "1T25" or "1Q25" (previous period in quarterly mode)
  result = result.replace(/\b2024\b/g, `1${q}25`);
  
  // Replace "Orçamento 2025" or "Budget 2025" with quarterly format
  if (language === 'pt') {
    result = result.replace(/Orçamento 2025/g, `Orçamento 1${q}26`);
    result = result.replace(/Orç\. 2025/g, `Orç. 1${q}26`);
  } else {
    result = result.replace(/Budget 2025/g, `Budget 1${q}26`);
    result = result.replace(/Bud\. 2025/g, `Bud. 1${q}26`);
  }
  
  return result;
};
